import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { catchError, map, Observable, of, switchMap, throwError } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';
import { MpsApiService } from '../../../mps/services/mps-api.service';
import { MpsNotificationService } from '../../../mps/services/mps-notification.service';
import { applicationEndpoints } from '../../application-endpoints';
import { UsersQueryParams } from '../../interfaces/query-params';
import { LaravelDefaultResponse, PaginatedTypedListResponse } from '../../interfaces/responses';
import { User } from './user.model';

@Injectable({ providedIn: 'root' })
export class UsersService {
  private apiService = inject(MpsApiService);
  private notificationService = inject(MpsNotificationService);
  private translateService = inject(TranslateService);
  private queryParams = signal<UsersQueryParams | null>(null);
  private queryParams$ = toObservable<UsersQueryParams | null>(this.queryParams);
  isLoading = signal(true);

  private users$: Observable<PaginatedTypedListResponse<User>> = this.queryParams$.pipe(
    switchMap((queryParams: UsersQueryParams | null) => {
      if (queryParams) {
        return this.apiService
          .get<
            LaravelDefaultResponse<PaginatedTypedListResponse<User>>
          >(applicationEndpoints.users + '?' + this.mapQueryParams(queryParams))
          .pipe(
            map((response) => {
              this.isLoading.set(false);
              return response.data;
            })
          );
      }
      return of();
    })
  );

  usersResponse = toSignal(this.users$, {
    initialValue: {
      data: [] as User[],
      count: 0,
    } as PaginatedTypedListResponse<User>,
  });

  users = computed(() => this.usersResponse()?.data ?? []);
  resultsLength = computed(() => this.usersResponse()?.count ?? 0);

  selectedUser = signal<User | undefined>(undefined);

  userSelected(id: number): void {
    const user = this.users().find((user) => user.id === id);
    this.selectedUser.set(user);
  }

  refreshUsers(queryParams: UsersQueryParams): void {
    this.queryParams.set(queryParams);
  }

  all(search: string, roleId: number | null): Observable<User[]> {
    let query = `search=${search}`;
    if (roleId !== null) {
      query += `&roleId=${roleId}`;
    }

    return this.apiService.get<LaravelDefaultResponse<User[]>>(applicationEndpoints.all + '?' + query).pipe(
      map((response) => response.data),
      catchError((error: string) => {
        this.notificationService.error(this.translateService.instant('messages.api_response.generic_error'));
        return throwError(() => error);
      })
    );
  }

  usersToImpersonate(search: string): Observable<User[]> {
    return this.apiService
      .get<LaravelDefaultResponse<User[]>>(applicationEndpoints.usersToImpersonate + '?' + `search=${search}&`)
      .pipe(
        map((response) => response.data),
        catchError((error: string) => {
          this.notificationService.error(this.translateService.instant('messages.api_response.generic_error'));
          return throwError(() => error);
        })
      );
  }

  impersonate(userId: number): Observable<string> {
    return this.apiService
      .get<
        LaravelDefaultResponse<{ token: string }>
      >(`${applicationEndpoints.impersonateUser.replace(':userId', userId.toString())}`)
      .pipe(
        map((response) => response.data.token),
        catchError((error: string) => {
          this.notificationService.error(this.translateService.instant('messages.api_response.generic_error'));
          return throwError(() => error);
        })
      );
  }

  private mapQueryParams(params: UsersQueryParams) {
    // Page
    let queryParams = params.page || params.page === 0 ? `page=${params.page}&` : `page=0&`;
    queryParams += params.pageSize ? `pageSize=${params.pageSize}&` : `pageSize=20&`;

    // Order
    queryParams += params.sortField ? `sortField=${params.sortField}&` : '';
    queryParams += params.sortOrder ? `sortOrder=${params.sortOrder}&` : '';

    queryParams += params.search ? `search=${params.search}&` : '';

    return queryParams;
  }

  createUser(user: User): Observable<LaravelDefaultResponse<User>> {
    return this.apiService.post<User, LaravelDefaultResponse<User>>(applicationEndpoints.users, user).pipe(
      catchError((error: string) => {
        this.notificationService.error(error);
        return throwError(() => error);
      })
    );
  }

  updateUser(userId: number, data: User): Observable<LaravelDefaultResponse<User>> {
    return this.apiService
      .patch<User, LaravelDefaultResponse<User>>(`${applicationEndpoints.users}/${userId}`, data)
      .pipe(
        catchError((error: string) => {
          this.notificationService.error(error);
          return throwError(() => error);
        })
      );
  }

  deleteUser(userId: number): Observable<User> {
    return this.apiService.delete<User>(`${applicationEndpoints.users}/${userId}`).pipe(
      catchError((error: string) => {
        this.notificationService.error(error);
        return throwError(() => error);
      })
    );
  }
}
